import type { Collider } from "../camera/walk.js";

/**
 * Sizes of an elevator bank (meters). Everything is expressed in the bank's own frame: origin at
 * the middle of the shaft on the floor, `+x` across the doors, `+z` out into the hall, `+y` up.
 */
export const SHAFT = {
  /** Outer size of the (square) shaft block. */
  size: 2.4,
  /** Thickness of the shaft walls. */
  wall: 0.14,
  /** Clear width of the door opening. */
  door: 1.2,
  /** Height of the door opening. */
  doorH: 2.2,
  /** Outer width of the cabin. */
  cabin: 2.08,
  /** Walls of the cabin. */
  cabinWall: 0.04,
  /** Clear height inside the cabin. */
  cabinH: 2.45,
  /** The cabin floor sits this far under the walking surface. */
  cabinFloor: 0.06,
} as const;

/** Where a cabin stands on every floor (building local frame) and which way its doors face. */
export interface CabinSlot {
  x: number;
  z: number;
  /** Yaw of the bank frame: its local +z points out into the hall. */
  yaw: number;
}

/** A bank-frame offset (lateral, front) into the building's local frame. */
export function bankToLocal(slot: CabinSlot, lx: number, lz: number): [number, number] {
  const c = Math.cos(slot.yaw);
  const s = Math.sin(slot.yaw);
  return [slot.x + lx * c + lz * s, slot.z - lx * s + lz * c];
}

/** The building-local point to the bank frame (lateral, front). */
export function localToBank(slot: CabinSlot, x: number, z: number): [number, number] {
  const dx = x - slot.x;
  const dz = z - slot.z;
  const c = Math.cos(slot.yaw);
  const s = Math.sin(slot.yaw);
  return [dx * c - dz * s, dx * s + dz * c];
}

/** A box in the bank frame as an axis-aligned collider (banks face a wall, so the turn is a quarter). */
const box = (slot: CabinSlot, lx: number, lz: number, hw: number, hd: number): Collider => {
  const [x, z] = bankToLocal(slot, lx, lz);
  const quarter = Math.abs(Math.sin(slot.yaw)) > 0.5;
  return { x, z, yaw: 0, hw: quarter ? hd : hw, hd: quarter ? hw : hd, top: 3.7 };
};

/** The fixed walls of a shaft: back, both sides and the two jambs beside the door opening. */
export function shaftWalls(slot: CabinSlot): Collider[] {
  const h = SHAFT.size / 2;
  const w = SHAFT.wall / 2;
  const jamb = (h - SHAFT.door / 2) / 2;
  const jx = SHAFT.door / 2 + jamb;
  return [
    box(slot, 0, -(h - w), h, w),
    box(slot, -(h - w), 0, w, h),
    box(slot, h - w, 0, w, h),
    box(slot, -jx, h - w, jamb, w),
    box(slot, jx, h - w, jamb, w),
  ];
}

/** The closed doors as a solid across the opening (walkers cannot pass until they are open). */
export function doorBlock(slot: CabinSlot): Collider {
  return box(slot, 0, SHAFT.size / 2 - SHAFT.wall / 2, SHAFT.door / 2, SHAFT.wall / 2);
}

/** Door opening fraction (0 closed, 1 open) from which a walker can pass. */
export const PASSABLE_OPEN = 0.85;
